import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  type AlertTaskConfig,
  type AlertTaskConfigUpsert,
  TaskAssignmentStrategy,
  TaskDefaultFilter,
  type TaskSetting,
  type TaskSettingUpdate,
} from "@/types/task";

import {
  bulkUpsertAlertTaskConfigs,
  fetchAlertTaskConfigs,
  fetchTaskSettings,
  updateTaskSettings,
  upsertAlertTaskConfig,
} from "./task-settings";
import { taskQueryKeys } from "./tasks-query";

export const taskSettingsQueryKeys = {
  all: ["task-settings"] as const,
  settings: () => [...taskSettingsQueryKeys.all, "settings"] as const,
  alertConfigs: () => [...taskSettingsQueryKeys.all, "alert-configs"] as const,
};

// Hooks
export function useTaskSettings(enabled = true) {
  return useQuery({
    queryKey: taskSettingsQueryKeys.settings(),
    queryFn: fetchTaskSettings,
    enabled,
    select: (data: TaskSetting | null) =>
      data ?? {
        suppressTaskNotifications: true,
        completedTaskRetentionDays: 7,
        defaultTaskFilter: TaskDefaultFilter.SUBSCRIBED_TO,
        assignmentStrategy: TaskAssignmentStrategy.PATIENT_INTERACTION,
        respectShifts: true,
      },
  });
}

export function useAlertTaskConfigs() {
  return useQuery({
    queryKey: taskSettingsQueryKeys.alertConfigs(),
    queryFn: fetchAlertTaskConfigs,
    select: (data: AlertTaskConfig[] | null) => data ?? [],
  });
}

export function useTaskSettingsMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: TaskSettingUpdate) => updateTaskSettings(data),
    onMutate: async (data) => {
      await queryClient.cancelQueries({
        queryKey: taskSettingsQueryKeys.settings(),
      });
      const previousSettings = queryClient.getQueryData<TaskSetting>(
        taskSettingsQueryKeys.settings(),
      );
      queryClient.setQueryData<TaskSetting>(
        taskSettingsQueryKeys.settings(),
        (old) => ({
          suppressTaskNotifications: true,
          completedTaskRetentionDays: 7,
          defaultTaskFilter: TaskDefaultFilter.SUBSCRIBED_TO,
          assignmentStrategy: TaskAssignmentStrategy.PATIENT_INTERACTION,
          respectShifts: true,
          ...old,
          ...data,
        }),
      );
      return { previousSettings };
    },
    onError: (err, variables, context) => {
      if (context?.previousSettings) {
        queryClient.setQueryData(
          taskSettingsQueryKeys.settings(),
          context.previousSettings,
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: taskSettingsQueryKeys.settings(),
      });
      queryClient.invalidateQueries({
        queryKey: taskQueryKeys.counts(),
      });
    },
  });
}

export function useAlertTaskConfigMutations() {
  const queryClient = useQueryClient();

  const invalidateConfigs = () => {
    queryClient.invalidateQueries({
      queryKey: taskSettingsQueryKeys.alertConfigs(),
    });
  };

  return {
    upsert: useMutation({
      mutationFn: ({
        alertType,
        data,
      }: {
        alertType: string;
        data: AlertTaskConfigUpsert;
      }) => upsertAlertTaskConfig(alertType, data),
      onMutate: async ({ alertType, data }) => {
        await queryClient.cancelQueries({
          queryKey: taskSettingsQueryKeys.alertConfigs(),
        });
        const previousConfigs = queryClient.getQueryData<AlertTaskConfig[]>(
          taskSettingsQueryKeys.alertConfigs(),
        );

        if (previousConfigs) {
          queryClient.setQueryData<AlertTaskConfig[]>(
            taskSettingsQueryKeys.alertConfigs(),
            (old) => {
              if (!old) return old;
              const index = old.findIndex((c) => c.alertType === alertType);
              if (index === -1) {
                return [
                  ...old,
                  {
                    id: `temp-${Date.now()}`,
                    alertType,
                    enabled: data.enabled,
                    priority: data.priority,
                  } as AlertTaskConfig,
                ];
              }
              const updated = [...old];
              updated[index] = {
                ...updated[index],
                ...data,
                enabled: data.enabled,
                priority: data.priority,
              };
              return updated;
            },
          );
        }
        return { previousConfigs };
      },
      onError: (err, newConfig, context) => {
        if (context?.previousConfigs) {
          queryClient.setQueryData(
            taskSettingsQueryKeys.alertConfigs(),
            context.previousConfigs,
          );
        }
      },
      onSettled: invalidateConfigs,
    }),

    bulkUpsert: useMutation({
      mutationFn: (configs: AlertTaskConfigUpsert[]) =>
        bulkUpsertAlertTaskConfigs({ configs }),
      onMutate: async (configs) => {
        await queryClient.cancelQueries({
          queryKey: taskSettingsQueryKeys.alertConfigs(),
        });
        const previousConfigs = queryClient.getQueryData<AlertTaskConfig[]>(
          taskSettingsQueryKeys.alertConfigs(),
        );
        queryClient.setQueryData<AlertTaskConfig[]>(
          taskSettingsQueryKeys.alertConfigs(),
          (old) => {
            if (!old) return old;
            const updated = [...old];
            for (const config of configs) {
              const index = updated.findIndex(
                (c) => c.alertType === config.alertType,
              );
              if (index === -1) {
                updated.push({
                  id: `temp-${Date.now()}`,
                  alertType: config.alertType,
                  enabled: config.enabled,
                  priority: config.priority,
                } as AlertTaskConfig);
              } else {
                updated[index] = {
                  ...updated[index],
                  ...config,
                };
              }
            }
            return updated;
          },
        );
        return { previousConfigs };
      },
      onError: (err, variables, context) => {
        if (context?.previousConfigs) {
          queryClient.setQueryData(
            taskSettingsQueryKeys.alertConfigs(),
            context.previousConfigs,
          );
        }
      },
      onSettled: invalidateConfigs,
    }),
  };
}
